import { Injectable } from '@angular/core';
import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot } from '@angular/router';
import { NavigationStateService } from '@core/navigation-state/services/navigation-state.service';

// Eligibility is driven by data: { reusable: true } on the route definition in app.routes.ts.
// Angular inherits data to child routes, so sub-routes are covered automatically.
// To add a new route: set data: { reusable: true } in app.routes.ts — no changes needed here.
function isEligibleRoute(route: ActivatedRouteSnapshot): boolean {
  // Walk up the snapshot tree — data is inherited so any ancestor flag covers this node.
  let node: ActivatedRouteSnapshot | null = route;
  while (node) {
    if (node.data?.['reusable']) return true;
    node = node.parent;
  }
  return false;
}

@Injectable()
export class PageReuseStrategy implements RouteReuseStrategy {

  constructor(private readonly stateService: NavigationStateService) {
    // DIAGNOSTIC: If this log does NOT appear on app boot, the provider is not
    // taking effect. Most common cause: registered before provideRouter() in app.config.ts.
    console.log('[PageReuse] ✅ PageReuseStrategy instantiated');
  }

  // Return true to keep the same component instance on same-route navigations
  // (e.g. query-param changes within the customer dashboard itself).
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const result = future.routeConfig === curr.routeConfig;
    if (!result) {
      console.log(`[PageReuse] shouldReuseRoute "${curr.routeConfig?.path}" → "${future.routeConfig?.path}"`);
    }
    return result;
  }

  // Only detach LEAF routes in REUSABLE_ROUTES, and only when navigating TO the dashboard.
  //
  // LEAF guard (route.children.length === 0): routes that have active children own
  // a <router-outlet> internally. Detaching them stores the nested outlet context,
  // and Angular's setRouterState walks it recursively — if the stored context has
  // any circular reference (known Angular 18 issue with nested outlets) it overflows
  // the call stack. Detaching only the leaf avoids this entirely; the shell component
  // recreates normally and the leaf content is reattached inside it.
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const url = getRouteUrl(route);
    const going = this.stateService.isGoingToDashboard();
    const eligible = isEligibleRoute(route);
    const isLeaf = route.children.length === 0;
    const result = going && eligible && isLeaf;
    console.log(`[PageReuse] shouldDetach "${url}" going=${going} eligible=${eligible} isLeaf=${isLeaf} → ${result}`);
    return result;
  }

  // Store the detached handle. No side-effects (signal consume, etc.) —
  // mode is managed entirely by NavigationStateService router events.
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) return;
    const url = getRouteUrl(route);
    console.log(`[PageReuse] store "${url}"`);
    this.stateService.storeHandle(url, handle);
    this.stateService.emitDetached(url);
  }

  // Reattach only when coming back from the dashboard AND a valid handle exists.
  //
  // Two guards mirror shouldDetach:
  // 1. route.component — skip loadChildren shell nodes (no component resolved yet)
  // 2. route.children.length === 0 — skip shell routes that own a <router-outlet>.
  //    The stored handle was for the LEAF child, not the shell. Returning true for
  //    the shell would mount the leaf's ComponentRef at the wrong outlet level,
  //    causing view mismatch errors. The shell must recreate normally; only the
  //    leaf (children=0) is reattached inside the shell's outlet.
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!route.component) return false;
    if (route.children.length > 0) return false;  // shell with inner outlet — skip
    if (!this.stateService.isReturningFromDashboard()) return false;
    const url = getRouteUrl(route);
    const canAttach = this.stateService.hasValidHandle(url);
    console.log(`[PageReuse] shouldAttach "${url}" → ${canAttach}`);
    return canAttach;
  }

  // Return the stored handle. Called only when shouldAttach returned true.
  // Emitting reattached lets NavigationInterceptorService skip snapshot restore
  // (the live component tree already has perfect state after reattach).
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const url = getRouteUrl(route);
    const handle = this.stateService.retrieveHandle(url);
    console.log(`[PageReuse] retrieve "${url}" → ${handle ? 'FOUND ✓' : 'null'}`);
    if (handle) {
      this.stateService.emitReattached(url);
    }
    return handle;
  }
}

// Walks the full parent chain of a route snapshot and assembles the complete
// URL path (e.g. child of /renewal-reports → /renewal-reports/renewals/plan-renewals).
function getRouteUrl(route: ActivatedRouteSnapshot): string {
  let path = '';
  let node: ActivatedRouteSnapshot | null = route;
  while (node) {
    const segment = node.url.map(s => s.path).join('/');
    if (segment) path = segment + (path ? '/' + path : '');
    node = node.parent;
  }
  return '/' + path;
}
